/*
  Pure Grounds: the suggested-product element and the shared add-to-cart helper.

  Defined once here, not inline in a snippet. The cart drawer re-renders its markup with innerHTML
  (assets/cart-drawer.js, assets/cart.js), and browsers never run scripts inserted that way, so markup
  inserted later binds itself through connectedCallback. Used by the drip add-on, the cart bar's offers
  and the product-page "Pair it with" section.

  Loaded with defer from layout/theme.liquid (drawer carts), sections/main-cart-footer.liquid and
  snippets/product-addon.liquid. Safe to load twice: the define is guarded and the helper is idempotent.
*/
window.PGC = window.PGC || {};

/**
 * Adds items to the cart through /cart/add.js and resolves with the parsed response.
 * Pass `sections` (section ids) to get rendered sections back, the way Dawn's product form does.
 *
 * @param {Array<{id: number, quantity: number, properties?: Object}>} items
 * @param {{sections?: string[], sectionsUrl?: string}} [options]
 * @returns {Promise<Object>}
 */
window.PGC.cartAdd = function (items, options) {
  const opts = options || {};
  const body = { items: items };

  if (opts.sections && opts.sections.length) {
    body.sections = opts.sections;
    body.sections_url = opts.sectionsUrl || window.location.pathname;
  }

  const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

  return fetch(addUrl + '.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
    body: JSON.stringify(body),
  }).then((response) => response.json());
};

if (!customElements.get('product-addon')) {
  customElements.define(
    'product-addon',
    class ProductAddon extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('.product-addon__button');
        this.select = this.querySelector('.product-addon__select[name="id"]');
        this.errorEl = this.querySelector('.product-addon__error');

        if (!this.button || this.button.dataset.bound === 'true') return;
        this.button.dataset.bound = 'true';
        this.button.addEventListener('click', this.addToCart.bind(this));
      }

      get variantId() {
        return this.select ? this.select.value : this.dataset.variantId;
      }

      /** Line item properties from any properties[...] control inside the card (the grind select). */
      get properties() {
        const props = {};
        this.querySelectorAll('[name^="properties["]').forEach((el) => {
          const match = el.name.match(/^properties\[(.+)\]$/);
          if (match && el.value) props[match[1]] = el.value;
        });
        return Object.keys(props).length ? props : undefined;
      }

      /** 'page' inside the cart page footer, otherwise the drawer or the notification popup. */
      get context() {
        if (this.dataset.context === 'page') return 'page';
        return 'drawer';
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }

      addToCart() {
        const variantId = this.variantId;
        if (!variantId) return;

        this.clearError();
        this.button.setAttribute('aria-busy', 'true');
        this.button.disabled = true;

        const item = { id: Number(variantId), quantity: 1 };
        const properties = this.properties;
        if (properties) item.properties = properties;

        const cartItems = this.context === 'page' ? document.querySelector('cart-items') : null;
        const cartElement =
          document.querySelector('cart-drawer') || document.querySelector('cart-notification');

        let sections;
        if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
          sections = cartItems.getSectionsToRender().map((section) => section.section);
        } else if (cartElement && typeof cartElement.getSectionsToRender === 'function') {
          sections = cartElement.getSectionsToRender().map((section) => section.id);
        }

        window.PGC.cartAdd([item], { sections: sections })
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message);
              return;
            }

            if (cartItems && response.sections) {
              // Cart page: swap the same sections Dawn's quantity controls swap, without opening the drawer.
              cartItems.getSectionsToRender().forEach((section) => {
                const container = document.getElementById(section.id);
                if (!container || !response.sections[section.section]) return;
                const target = container.querySelector(section.selector) || container;
                target.innerHTML = cartItems.getSectionInnerHTML(response.sections[section.section], section.selector);
              });
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-bar', cartData: response });
              }
            } else if (cartElement && typeof cartElement.renderContents === 'function') {
              cartElement.renderContents(response);
            } else {
              window.location = (window.routes && window.routes.cart_url) || '/cart';
            }

            document.dispatchEvent(new CustomEvent('product-addon:added', { detail: response }));
          })
          .catch(() => {
            this.showError('Sorry, that did not go through. Please try again.');
          })
          .finally(() => {
            this.button.disabled = false;
            this.button.removeAttribute('aria-busy');
          });
      }
    }
  );
}

/*
  Hero test measurement (snippets/hero-test-assign.liquid sets window.PGC_HERO). Three things, none with
  personal data: the group is written onto the cart as the attribute hero_test at the first cart change,
  so every order carries it; one "session" event per browser session and a "buy_now" event per hero tap
  are published as Shopify customer events (pgc_hero_test) and sent as a beacon to the storefront tools
  Worker (data-pgc-worker on the html element) when its URL is set.
*/
(function () {
  var hero = window.PGC_HERO;
  if (!hero || !hero.group) return;
  var endpoint = (document.documentElement.getAttribute('data-pgc-worker') || '').trim().replace(/\/+$/, '');

  function send(step, extra) {
    var payload = Object.assign({ group: hero.group, step: step, path: location.pathname }, extra || {});
    hero.events.push(payload);
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      try {
        window.Shopify.analytics.publish('pgc_hero_test', payload);
      } catch (e) {
        /* analytics not ready */
      }
    }
    if (endpoint && navigator.sendBeacon) {
      try {
        navigator.sendBeacon(endpoint + '/event', new Blob([JSON.stringify(payload)], { type: 'text/plain' }));
      } catch (e) {
        /* offline */
      }
    }
  }

  var counted = false;
  try {
    counted = sessionStorage.getItem('pgc_hero_session') === hero.group;
  } catch (e) {
    counted = false;
  }
  if (!counted) {
    try {
      sessionStorage.setItem('pgc_hero_session', hero.group);
    } catch (e) {
      /* storage off */
    }
    send('session', { fresh: hero.fresh });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('.quick-buy__button');
    if (button) send('buy_now', { product: (button.getAttribute('data-product-url') || '').split('/').pop() });
  });

  var stamped = false;
  try {
    stamped = sessionStorage.getItem('pgc_hero_stamped') === hero.group;
  } catch (e) {
    stamped = false;
  }
  function stamp() {
    if (stamped) return;
    stamped = true;
    fetch(((window.routes && window.routes.cart_update_url) || '/cart/update') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { hero_test: hero.group } }),
    })
      .then(function (response) {
        if (!response.ok) throw new Error(String(response.status));
        try {
          sessionStorage.setItem('pgc_hero_stamped', hero.group);
        } catch (e) {
          /* storage off */
        }
      })
      .catch(function () {
        stamped = false;
      });
  }
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, stamp);
  }
  document.addEventListener('product-addon:added', stamp);
  document.addEventListener('pair-with:added', stamp);
})();
